import { isFieldBinding } from "@redotech/effect-api-shared/default-rules/field-binding";
import type {
  ConditionEvaluationResult,
  ValuesAtExecution,
} from "@redotech/effect-api-shared/models/shopper-ai/rule-engine-results";
import {
  aiStatusConditionSchema,
  assignmentV3ConditionSchema,
  ContactChannel,
  lastOrderConditionSchema,
  LastOrderProperty,
  messageCountConditionSchema,
  messageTypeV3ConditionSchema,
  previousTicketStatusConditionSchema,
  randomPercentageConditionSchema,
  receivedAtConditionSchema,
  responseTimeConditionSchema,
  ResponseTimeOperator,
  ResponseType,
  sentFromConditionSchema,
  shopperAgentOutreachConditionSchema,
  ticketChannelV3ConditionSchema,
  ticketStatusV3ConditionSchema,
  timeBasedConditionSchema,
  TimeBasedMode,
  type AiStatusCondition,
  type AssignmentV3Condition,
  type LastOrderCondition,
  type MessageCountCondition,
  type MessageTypeV3Condition,
  type PreviousTicketStatusCondition,
  type RandomPercentageCondition,
  type ReceivedAtCondition,
  type ResponseTimeCondition,
  type RuleV3Condition,
  type SentFromCondition,
  type ShopperAgentOutreachCondition,
  type TicketChannelV3Condition,
  type TicketStatusV3Condition,
  type TimeBasedCondition,
} from "@redotech/redo-flows-common/rules-v3/schemas";
import {
  shopperAgentOutreachConditionLabel,
  TimeUnit,
} from "@redotech/redo-flows-common/rules-v3/types";
import type {
  HasTagCondition,
  MessageBodyMatchesCondition,
  MessageSentimentCondition,
  SubjectLineCondition,
  TopicCondition,
  TriggerTagCondition,
} from "@redotech/redo-flows-common/rules/schemas";
import {
  hasTagConditionSchema,
  messageBodyMatchesConditionSchema,
  messageSentimentConditionSchema,
  subjectLineConditionSchema,
  topicConditionSchema,
  triggerTagConditionSchema,
} from "@redotech/redo-flows-common/rules/schemas";
import {
  DateQueryOperator,
  MESSAGE_CONDITION_TRIGGER_TYPES,
  MESSAGE_ONLY_CONDITION_TYPES,
  NumericQueryOperator,
  QueryOperator,
  RuleConditionType,
  RuleSubjectKind,
  subjectKindForCondition,
  subjectKindForTrigger,
  TAG_TRIGGER_CONDITION_TRIGGER_TYPES,
  TAG_TRIGGER_ONLY_CONDITION_TYPES,
  type RuleTriggerType,
  type SelectionQueryOperator,
  type TextQueryOperator,
} from "@redotech/redo-flows-common/rules/types";
import { t, useTranslation } from "@redotech/redo-i18n";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { memo, useCallback, useMemo } from "react";
import { useDefaultRuleEditContext } from "../default-rule-edit-context";
import { AiStatusCondition as AiStatusConditionComponent } from "../flow-editor/conditions/ai-status-condition";
import { AssignmentCondition as AssignmentConditionComponent } from "../flow-editor/conditions/assignment-condition";
import { HasTagCondition as HasTagConditionComponent } from "../flow-editor/conditions/has-tag-condition";
import { LastOrderCondition as LastOrderConditionComponent } from "../flow-editor/conditions/last-order-condition";
import { MessageBodyMatchesCondition as MessageBodyMatchesConditionComponent } from "../flow-editor/conditions/message-body-matches-condition";
import { MessageCountCondition as MessageCountConditionComponent } from "../flow-editor/conditions/message-count-condition";
import { MessageSentimentCondition as MessageSentimentConditionComponent } from "../flow-editor/conditions/message-sentiment-condition";
import { MessageTypeV3Condition as MessageTypeV3ConditionComponent } from "../flow-editor/conditions/message-type-v3-condition";
import { RandomPercentageCondition as RandomPercentageConditionComponent } from "../flow-editor/conditions/random-percentage-condition";
import { ReceivedAtCondition as ReceivedAtConditionComponent } from "../flow-editor/conditions/received-at-condition";
import { ResponseTimeCondition as ResponseTimeConditionComponent } from "../flow-editor/conditions/response-time-condition";
import { SentFromCondition as SentFromConditionComponent } from "../flow-editor/conditions/sent-from-condition";
import { ShopperAgentOutreachCondition as ShopperAgentOutreachConditionComponent } from "../flow-editor/conditions/shopper-agent-outreach-condition";
import { SubjectLineCondition as SubjectLineConditionComponent } from "../flow-editor/conditions/subject-line-condition";
import { TicketChannelV3Condition as TicketChannelV3ConditionComponent } from "../flow-editor/conditions/ticket-channel-v3-condition";
import { TicketStatusV3Condition as TicketStatusV3ConditionComponent } from "../flow-editor/conditions/ticket-status-v3-condition";
import { TimeBasedCondition as TimeBasedConditionComponent } from "../flow-editor/conditions/time-based-condition";
import { TopicCondition as TopicConditionComponent } from "../flow-editor/conditions/topic-condition";
import { TriggerTagCondition as TriggerTagConditionComponent } from "../flow-editor/conditions/trigger-tag-condition";
import {
  dateOperatorOptions,
  getOperatorOptionsForConditionType,
  numericOperatorOptions,
  selectionOperatorOptions,
  type AnyOperator,
} from "./condition-item";

// V3-only operator options for Ticket Status — extends is/is not with "has been"
const ticketStatusV3OperatorOptions = () => [
  ...selectionOperatorOptions(),
  {
    id: QueryOperator.HAS_BEEN,
    type: "text" as const,
    text: t("has been"),
    value: QueryOperator.HAS_BEEN,
  },
];

const conditionTypeOptions = (): {
  id: string;
  type: "text";
  text: string;
  value: RuleConditionType;
}[] => [
  {
    id: RuleConditionType.USER_IS_AVAILABLE,
    type: "text",
    text: t("Teammate availability"),
    value: RuleConditionType.USER_IS_AVAILABLE,
  },
  {
    id: RuleConditionType.TICKET_CHANNEL,
    type: "text",
    text: t("Ticket channel"),
    value: RuleConditionType.TICKET_CHANNEL,
  },
  {
    id: RuleConditionType.TICKET_STATUS,
    type: "text",
    text: t("Ticket status"),
    value: RuleConditionType.TICKET_STATUS,
  },
  {
    id: RuleConditionType.TOPIC,
    type: "text",
    text: t("Topic"),
    value: RuleConditionType.TOPIC,
  },
  {
    id: RuleConditionType.SENT_FROM,
    type: "text",
    text: t("Sent from"),
    value: RuleConditionType.SENT_FROM,
  },
  {
    id: RuleConditionType.RECEIVED_AT,
    type: "text",
    text: t("Received at"),
    value: RuleConditionType.RECEIVED_AT,
  },
  {
    id: RuleConditionType.SUBJECT_LINE,
    type: "text",
    text: t("Subject line"),
    value: RuleConditionType.SUBJECT_LINE,
  },
  {
    id: RuleConditionType.MESSAGE_BODY_MATCHES,
    type: "text",
    text: t("Message body matches"),
    value: RuleConditionType.MESSAGE_BODY_MATCHES,
  },
  {
    id: RuleConditionType.TIME_BASED,
    type: "text",
    text: t("Time based"),
    value: RuleConditionType.TIME_BASED,
  },
  {
    id: RuleConditionType.ASSIGNMENT,
    type: "text",
    text: t("Assignment"),
    value: RuleConditionType.ASSIGNMENT,
  },
  {
    id: RuleConditionType.LAST_ORDER,
    type: "text",
    text: t("Customer last order"),
    value: RuleConditionType.LAST_ORDER,
  },
  {
    id: RuleConditionType.MESSAGE_SENTIMENT,
    type: "text",
    text: t("Message sentiment"),
    value: RuleConditionType.MESSAGE_SENTIMENT,
  },
  {
    id: RuleConditionType.HAS_TAG,
    type: "text",
    text: t("Has tag"),
    value: RuleConditionType.HAS_TAG,
  },
  {
    id: RuleConditionType.TRIGGER_TAG,
    type: "text",
    text: t("Trigger tag"),
    value: RuleConditionType.TRIGGER_TAG,
  },
  {
    id: RuleConditionType.RESPONSE_TIME,
    type: "text",
    text: t("Time since last response"),
    value: RuleConditionType.RESPONSE_TIME,
  },
  {
    id: RuleConditionType.AI_STATUS,
    type: "text",
    text: t("AI handed off"),
    value: RuleConditionType.AI_STATUS,
  },
  {
    id: RuleConditionType.MESSAGE_COUNT,
    type: "text",
    text: t("Message count"),
    value: RuleConditionType.MESSAGE_COUNT,
  },
  {
    id: RuleConditionType.MESSAGE_TYPE,
    type: "text",
    text: t("Message type"),
    value: RuleConditionType.MESSAGE_TYPE,
  },
  {
    id: RuleConditionType.RANDOM_PERCENTAGE,
    type: "text",
    text: t("Random percentage"),
    value: RuleConditionType.RANDOM_PERCENTAGE,
  },
  {
    id: RuleConditionType.SHOPPER_AGENT_OUTREACH,
    type: "text",
    text: t("{{productName}} outreach", { productName: "Shopper Agent" }),
    value: RuleConditionType.SHOPPER_AGENT_OUTREACH,
  },
];

export const ConditionItemV3 = memo(function ConditionItemV3({
  condition,
  onChange,
  onDelete,
  triggerType,
  executionResult,
  valuesAtExecution,
  readonly,
}: {
  condition: RuleV3Condition;
  onChange: (condition: RuleV3Condition) => void;
  onDelete: () => void;
  triggerType?: RuleTriggerType;
  executionResult?: ConditionEvaluationResult;
  valuesAtExecution?: ValuesAtExecution;
  readonly?: boolean;
}) {
  const { t } = useTranslation();
  // When rendered inside a default-rule edit page the condition structure
  // is fixed by the rule's template. Force readonly on the type/operator/
  // delete controls, and derive per-condition `editableFields` for the
  // leaf editor from `FieldBinding` markers in the matching sub-template.
  const editCtx = useDefaultRuleEditContext();
  const effectiveReadonly = readonly || editCtx !== null;
  const templateCondition =
    editCtx && condition.conditionType
      ? editCtx.findConditionTemplate(condition.conditionType)
      : undefined;
  const editableFields = useMemo<ReadonlySet<string> | undefined>(() => {
    if (!editCtx) {
      return undefined;
    }
    const set = new Set<string>();
    if (templateCondition) {
      // Recurse into nested objects so bindings on inner fields (e.g.
      // `mode.assignmentTypes` on the assignment condition) are emitted as
      // dotted paths and the leaf editor can gate sub-controls on them.
      const visit = (prefix: string, obj: unknown) => {
        if (!obj || typeof obj !== "object") {
          return;
        }
        for (const [key, value] of Object.entries(obj)) {
          const path = prefix ? `${prefix}.${key}` : key;
          if (isFieldBinding(value)) {
            set.add(path);
          } else if (value && typeof value === "object") {
            visit(path, value);
          }
        }
      };
      visit("", templateCondition);
    }
    return set;
  }, [editCtx, templateCondition]);

  const isMessageTrigger =
    triggerType &&
    (MESSAGE_CONDITION_TRIGGER_TYPES as readonly string[]).includes(
      triggerType,
    );
  const isTagTrigger =
    triggerType &&
    (TAG_TRIGGER_CONDITION_TRIGGER_TYPES as readonly string[]).includes(
      triggerType,
    );
  // A ticket condition can't be answered about a user, and vice versa. An
  // unset trigger falls back to the ticket subject.
  const subjectKind =
    triggerType === undefined
      ? RuleSubjectKind.TICKET
      : subjectKindForTrigger(triggerType);
  const filteredConditionTypeOptions = conditionTypeOptions().filter((opt) => {
    if (subjectKindForCondition(opt.value) !== subjectKind) {
      return false;
    }
    if (
      !isMessageTrigger &&
      (MESSAGE_ONLY_CONDITION_TYPES as readonly string[]).includes(opt.value)
    ) {
      return false;
    }
    if (
      !isTagTrigger &&
      (TAG_TRIGGER_ONLY_CONDITION_TYPES as readonly string[]).includes(
        opt.value,
      )
    ) {
      return false;
    }
    return true;
  });
  const handleTypeChange = useCallback(
    (newType: RuleConditionType) => {
      let newCondition: RuleV3Condition;

      switch (newType) {
        case RuleConditionType.TICKET_CHANNEL:
          newCondition = {
            conditionType: RuleConditionType.TICKET_CHANNEL,
            channels: [],
            queryOperator: QueryOperator.IS,
          } satisfies TicketChannelV3Condition;
          break;
        case RuleConditionType.TICKET_STATUS:
          newCondition = {
            conditionType: RuleConditionType.TICKET_STATUS,
            statuses: [],
            queryOperator: QueryOperator.IS,
          } satisfies TicketStatusV3Condition;
          break;
        case RuleConditionType.TOPIC:
          newCondition = {
            conditionType: RuleConditionType.TOPIC,
            topics: [],
            queryOperator: QueryOperator.IS,
          } satisfies TopicCondition;
          break;
        case RuleConditionType.SENT_FROM:
          newCondition = {
            conditionType: RuleConditionType.SENT_FROM,
            channel: ContactChannel.EMAIL,
            values: [],
            queryOperator: QueryOperator.CONTAINS,
          } satisfies SentFromCondition;
          break;
        case RuleConditionType.RECEIVED_AT:
          newCondition = {
            conditionType: RuleConditionType.RECEIVED_AT,
            channel: ContactChannel.EMAIL,
            values: [],
            queryOperator: QueryOperator.IS_EXACTLY,
          } satisfies ReceivedAtCondition;
          break;
        case RuleConditionType.SUBJECT_LINE:
          newCondition = {
            conditionType: RuleConditionType.SUBJECT_LINE,
            queryStrings: [],
            caseSensitive: false,
            queryOperator: QueryOperator.CONTAINS,
          } satisfies SubjectLineCondition;
          break;
        case RuleConditionType.MESSAGE_BODY_MATCHES:
          newCondition = {
            conditionType: RuleConditionType.MESSAGE_BODY_MATCHES,
            queryStrings: [],
            caseSensitive: false,
            queryOperator: QueryOperator.CONTAINS,
          } satisfies MessageBodyMatchesCondition;
          break;
        case RuleConditionType.TIME_BASED:
          newCondition = {
            conditionType: RuleConditionType.TIME_BASED,
            mode: TimeBasedMode.BUSINESS_HOURS,
          } satisfies TimeBasedCondition;
          break;
        case RuleConditionType.ASSIGNMENT:
          newCondition = {
            conditionType: RuleConditionType.ASSIGNMENT,
            mode: { _tag: "type", assignmentTypes: [] },
            queryOperator: QueryOperator.IS,
          } satisfies AssignmentV3Condition;
          break;
        case RuleConditionType.LAST_ORDER:
          newCondition = {
            conditionType: RuleConditionType.LAST_ORDER,
            property: LastOrderProperty.TIME,
            queryOperator: DateQueryOperator.WITHIN_LAST,
            unit: TimeUnit.DAYS,
          } satisfies LastOrderCondition;
          break;
        case RuleConditionType.MESSAGE_SENTIMENT:
          newCondition = {
            conditionType: RuleConditionType.MESSAGE_SENTIMENT,
            sentiments: [],
            queryOperator: QueryOperator.IS,
          } satisfies MessageSentimentCondition;
          break;
        case RuleConditionType.HAS_TAG:
          newCondition = {
            conditionType: RuleConditionType.HAS_TAG,
            tags: [],
            queryOperator: QueryOperator.IS,
          } satisfies HasTagCondition;
          break;
        case RuleConditionType.TRIGGER_TAG:
          newCondition = {
            conditionType: RuleConditionType.TRIGGER_TAG,
            tags: [],
            queryOperator: QueryOperator.IS,
          } satisfies TriggerTagCondition;
          break;
        case RuleConditionType.RESPONSE_TIME:
          newCondition = {
            conditionType: RuleConditionType.RESPONSE_TIME,
            responseType: ResponseType.EITHER,
            responseTimeOperator: ResponseTimeOperator.MORE_THAN,
            value: 0,
            unit: TimeUnit.HOURS,
          } satisfies ResponseTimeCondition;
          break;
        case RuleConditionType.AI_STATUS:
          newCondition = {
            conditionType: RuleConditionType.AI_STATUS,
            statuses: ["HANDED_OFF"],
            queryOperator: QueryOperator.IS,
          } satisfies AiStatusCondition;
          break;
        case RuleConditionType.MESSAGE_COUNT:
          newCondition = {
            conditionType: RuleConditionType.MESSAGE_COUNT,
            queryOperator: NumericQueryOperator.GREATER_THAN,
            value: 0,
          } satisfies MessageCountCondition;
          break;
        case RuleConditionType.MESSAGE_TYPE:
          newCondition = {
            conditionType: RuleConditionType.MESSAGE_TYPE,
            messageTypes: [],
            queryOperator: QueryOperator.IS,
          } satisfies MessageTypeV3Condition;
          break;
        case RuleConditionType.RANDOM_PERCENTAGE:
          newCondition = {
            conditionType: RuleConditionType.RANDOM_PERCENTAGE,
            percentage: 50,
          } satisfies RandomPercentageCondition;
          break;
        case RuleConditionType.SHOPPER_AGENT_OUTREACH:
          newCondition = {
            conditionType: RuleConditionType.SHOPPER_AGENT_OUTREACH,
            outreachTypes: [],
            queryOperator: QueryOperator.IS,
          } satisfies ShopperAgentOutreachCondition;
          break;
        case RuleConditionType.AFTER_HOURS:
        case RuleConditionType.LAST_ORDER_TIME:
        case RuleConditionType.SENT_FROM_EMAIL:
        case RuleConditionType.RECEIVED_AT_EMAIL:
        case RuleConditionType.ASSIGNMENT_TYPE:
        case RuleConditionType.USER_IS_AVAILABLE:
          newCondition = {
            conditionType: RuleConditionType.USER_IS_AVAILABLE,
            queryOperator: QueryOperator.IS,
          };
          break;
        case RuleConditionType.PREVIOUS_TICKET_STATUS:
        case RuleConditionType.SHOPPER_AGENT_CONVERSATION:
        case RuleConditionType.MESSAGE_STORY_KIND:
          // V2-only or system-managed conditions; not user-selectable
          return;
        default:
          return assertNever(newType);
      }

      onChange(newCondition);
    },
    [onChange],
  );

  const renderConditionDetails = useMemo(() => {
    if (!condition.conditionType) {
      return null;
    }

    const conditionType = condition.conditionType;

    switch (conditionType) {
      case RuleConditionType.TIME_BASED:
        return (
          <TimeBasedConditionComponent
            condition={timeBasedConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.ASSIGNMENT:
        return (
          <AssignmentConditionComponent
            condition={assignmentV3ConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.MESSAGE_BODY_MATCHES:
        return (
          <MessageBodyMatchesConditionComponent
            condition={messageBodyMatchesConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.RECEIVED_AT:
        return (
          <ReceivedAtConditionComponent
            condition={receivedAtConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
            triggerType={triggerType}
          />
        );
      case RuleConditionType.SENT_FROM:
        return (
          <SentFromConditionComponent
            condition={sentFromConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
            triggerType={triggerType}
          />
        );
      case RuleConditionType.SUBJECT_LINE:
        return (
          <SubjectLineConditionComponent
            condition={subjectLineConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.TICKET_CHANNEL:
        return (
          <TicketChannelV3ConditionComponent
            condition={ticketChannelV3ConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.TICKET_STATUS:
        return (
          <TicketStatusV3ConditionComponent
            condition={ticketStatusV3ConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.PREVIOUS_TICKET_STATUS: {
        const parsed = previousTicketStatusConditionSchema.parse(condition);
        return (
          <TicketStatusV3ConditionComponent
            condition={{
              conditionType: RuleConditionType.TICKET_STATUS,
              statuses: parsed.statuses,
              queryOperator: parsed.queryOperator ?? QueryOperator.IS_NOT,
            }}
            editableFields={editableFields}
            onChange={(updated) =>
              onChange({
                conditionType: RuleConditionType.PREVIOUS_TICKET_STATUS,
                statuses: "statuses" in updated ? updated.statuses : [],
                queryOperator:
                  updated.queryOperator === QueryOperator.HAS_BEEN
                    ? QueryOperator.IS_NOT
                    : (updated.queryOperator as SelectionQueryOperator),
              } satisfies PreviousTicketStatusCondition)
            }
          />
        );
      }
      case RuleConditionType.TOPIC:
        return (
          <TopicConditionComponent
            condition={topicConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.LAST_ORDER:
        return (
          <LastOrderConditionComponent
            condition={lastOrderConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.MESSAGE_SENTIMENT:
        return (
          <MessageSentimentConditionComponent
            condition={messageSentimentConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.HAS_TAG:
        return (
          <HasTagConditionComponent
            condition={hasTagConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.TRIGGER_TAG:
        return (
          <TriggerTagConditionComponent
            condition={triggerTagConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.RESPONSE_TIME:
        return (
          <ResponseTimeConditionComponent
            condition={responseTimeConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.AI_STATUS:
        return (
          <AiStatusConditionComponent
            condition={aiStatusConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.MESSAGE_COUNT:
        return (
          <MessageCountConditionComponent
            condition={messageCountConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.MESSAGE_TYPE:
        return (
          <MessageTypeV3ConditionComponent
            condition={messageTypeV3ConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.RANDOM_PERCENTAGE:
        return (
          <RandomPercentageConditionComponent
            condition={randomPercentageConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      case RuleConditionType.SHOPPER_AGENT_OUTREACH:
        return (
          <ShopperAgentOutreachConditionComponent
            condition={shopperAgentOutreachConditionSchema.parse(condition)}
            editableFields={editableFields}
            onChange={onChange}
          />
        );
      // System-managed, or carried entirely by the is/is-not operator.
      case RuleConditionType.SHOPPER_AGENT_CONVERSATION:
      case RuleConditionType.USER_IS_AVAILABLE:
      case RuleConditionType.MESSAGE_STORY_KIND:
        return null;
      default:
        assertNever(conditionType);
    }
  }, [condition, onChange, editableFields, triggerType]);

  const operatorOptions = useMemo(() => {
    if (!condition.conditionType) {
      return null;
    }
    if (
      condition.conditionType === RuleConditionType.RESPONSE_TIME ||
      condition.conditionType === RuleConditionType.RANDOM_PERCENTAGE
    ) {
      return null;
    }
    if (condition.conditionType === RuleConditionType.TICKET_STATUS) {
      return ticketStatusV3OperatorOptions();
    }
    if (condition.conditionType === RuleConditionType.PREVIOUS_TICKET_STATUS) {
      return selectionOperatorOptions();
    }
    if (condition.conditionType === RuleConditionType.LAST_ORDER) {
      return condition.property === LastOrderProperty.TIME
        ? dateOperatorOptions()
        : numericOperatorOptions();
    }
    if (condition.conditionType === RuleConditionType.MESSAGE_COUNT) {
      return numericOperatorOptions();
    }
    return getOperatorOptionsForConditionType(condition.conditionType);
  }, [condition]);

  const handleOperatorChange = useCallback(
    (newOperator: AnyOperator) => {
      switch (condition.conditionType) {
        case RuleConditionType.TIME_BASED:
        case RuleConditionType.RESPONSE_TIME:
        case RuleConditionType.RANDOM_PERCENTAGE:
        case RuleConditionType.SHOPPER_AGENT_CONVERSATION:
        case RuleConditionType.MESSAGE_STORY_KIND:
          return;
        case RuleConditionType.TICKET_CHANNEL:
        case RuleConditionType.TOPIC:
        case RuleConditionType.ASSIGNMENT:
        case RuleConditionType.MESSAGE_SENTIMENT:
        case RuleConditionType.HAS_TAG:
        case RuleConditionType.TRIGGER_TAG:
        case RuleConditionType.AI_STATUS:
        case RuleConditionType.MESSAGE_TYPE:
        case RuleConditionType.PREVIOUS_TICKET_STATUS:
        case RuleConditionType.SHOPPER_AGENT_OUTREACH:
          onChange({
            ...condition,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            queryOperator: newOperator as SelectionQueryOperator,
          });
          break;
        case RuleConditionType.TICKET_STATUS: {
          const statuses = condition.statuses;
          if (newOperator === QueryOperator.HAS_BEEN) {
            onChange({
              conditionType: RuleConditionType.TICKET_STATUS,
              statuses,
              queryOperator: QueryOperator.HAS_BEEN,
              statusTimeOperator: ResponseTimeOperator.MORE_THAN,
              value: 0,
              unit: TimeUnit.HOURS,
            } satisfies TicketStatusV3Condition);
          } else {
            onChange({
              conditionType: RuleConditionType.TICKET_STATUS,
              statuses,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              queryOperator: newOperator as SelectionQueryOperator,
            } satisfies TicketStatusV3Condition);
          }
          break;
        }
        case RuleConditionType.SUBJECT_LINE:
        case RuleConditionType.MESSAGE_BODY_MATCHES:
        case RuleConditionType.SENT_FROM:
        case RuleConditionType.RECEIVED_AT:
          onChange({
            ...condition,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            queryOperator: newOperator as TextQueryOperator,
          });
          break;
        case RuleConditionType.LAST_ORDER:
          if (condition.property === LastOrderProperty.TIME) {
            onChange({
              ...condition,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
              queryOperator: newOperator as DateQueryOperator,
            });
          } else {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            const numOp = newOperator as NumericQueryOperator;
            if (numOp === NumericQueryOperator.BETWEEN) {
              onChange({
                conditionType: condition.conditionType,
                property: condition.property,
                queryOperator: numOp,
                minValue: 0,
                maxValue: 0,
              });
            } else {
              onChange({
                conditionType: condition.conditionType,
                property: condition.property,
                queryOperator: numOp,
                value: 0,
              });
            }
          }
          break;
        case RuleConditionType.MESSAGE_COUNT: {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          const numOp = newOperator as NumericQueryOperator;
          if (numOp === NumericQueryOperator.BETWEEN) {
            onChange({
              conditionType: condition.conditionType,
              queryOperator: numOp,
              minValue: 0,
              maxValue: 0,
              senderTypes: condition.senderTypes,
            });
          } else {
            onChange({
              conditionType: condition.conditionType,
              queryOperator: numOp,
              value: 0,
              senderTypes: condition.senderTypes,
            });
          }
          break;
        }
        case RuleConditionType.USER_IS_AVAILABLE:
          onChange({
            ...condition,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
            queryOperator: newOperator as SelectionQueryOperator,
          });
          break;
        default:
          assertNever(condition);
      }
    },
    [condition, onChange],
  );

  const currentOperator = useMemo(() => {
    if (!condition.conditionType || !operatorOptions) {
      return undefined;
    }
    const operator =
      "queryOperator" in condition ? condition.queryOperator : undefined;
    return operatorOptions.find((opt) => opt.value === operator);
  }, [condition, operatorOptions]);

  return (
    <Flex
      align="flex-start"
      bgColor="secondary"
      borderRadius="md"
      dir="column"
      gap="lg"
      p="lg"
    >
      <Flex align="center" gap="sm" justify="space-between" w="full">
        <Flex align="center" gap="sm" grow={1}>
          <RedoSingleSelectDropdownInput
            disabled={effectiveReadonly}
            options={filteredConditionTypeOptions}
            optionSelected={({ item }) => handleTypeChange(item.value)}
            placeholder={t("Select condition")}
            selectedItem={
              condition.conditionType
                ? filteredConditionTypeOptions.find(
                    (opt) => opt.value === condition.conditionType,
                  )
                : undefined
            }
          />
          {operatorOptions && (
            <RedoSingleSelectDropdownInput
              disabled={
                // In default-rule edit mode, operator is editable only
                // when the rule's template binds the condition's
                // `queryOperator` field. Otherwise honour the normal
                // readonly flag.
                editCtx !== null
                  ? !editableFields?.has("queryOperator")
                  : readonly
              }
              options={operatorOptions}
              optionSelected={({ item }) => handleOperatorChange(item.value)}
              selectedItem={currentOperator}
            />
          )}
        </Flex>
        {!effectiveReadonly && (
          <RedoButton
            aria-label={t("Delete")}
            hierarchy="tertiary"
            IconLeading={TrashIcon}
            onClick={onDelete}
            size="sm"
            theme="destructive"
          />
        )}
      </Flex>

      {condition.conditionType && renderConditionDetails}

      {executionResult && (
        <ExecutionAnnotation
          conditionType={condition.conditionType}
          result={executionResult}
          valuesAtExecution={valuesAtExecution}
        />
      )}
    </Flex>
  );
});

const ExecutionAnnotation = memo(function ExecutionAnnotation({
  conditionType,
  valuesAtExecution,
}: {
  conditionType: RuleConditionType;
  result: ConditionEvaluationResult;
  valuesAtExecution?: ValuesAtExecution;
}) {
  const { t } = useTranslation();
  if (!valuesAtExecution) {
    return null;
  }

  switch (conditionType) {
    case RuleConditionType.TICKET_STATUS: {
      const status = valuesAtExecution.conversation?.status;
      if (!status) {
        return null;
      }
      return (
        <Flex align="center" gap="xs" w="full">
          <Text fontSize="xs" textColor="secondary">
            {t("Current:")}
          </Text>
          <RedoBadge color="blue" size="sm" text={status} />
        </Flex>
      );
    }
    case RuleConditionType.TICKET_CHANNEL: {
      const channels = valuesAtExecution.channels?.integrationTypes;
      if (!channels) {
        return null;
      }
      return (
        <Flex align="center" gap="xs" w="full" wrap="wrap">
          <Text fontSize="xs" textColor="secondary">
            {t("Current:")}
          </Text>
          {channels.length > 0 ? (
            channels.map((ch) => (
              <RedoBadge color="blue" key={ch} size="sm" text={ch} />
            ))
          ) : (
            <Text fontSize="xs" textColor="secondary">
              {t("none")}
            </Text>
          )}
        </Flex>
      );
    }
    case RuleConditionType.ASSIGNMENT_TYPE: {
      const assignee = valuesAtExecution.conversation?.assigneeType;
      return (
        <Flex align="center" gap="xs" w="full">
          <Text fontSize="xs" textColor="secondary">
            {t("Current:")}
          </Text>
          <RedoBadge
            color="blue"
            size="sm"
            text={assignee ?? t("No assignee")}
          />
        </Flex>
      );
    }
    case RuleConditionType.SUBJECT_LINE: {
      const subjects = valuesAtExecution.threads?.emailSubjects;
      if (!subjects) {
        return null;
      }
      return (
        <Flex align="flex-start" dir="column" gap="xs" w="full">
          <Text fontSize="xs" textColor="secondary">
            {t("Current subjects:")}
          </Text>
          {subjects.length > 0 ? (
            subjects.map((s, i) => (
              <Text fontSize="xs" key={i} textColor="primary">
                {s}
              </Text>
            ))
          ) : (
            <Text fontSize="xs" textColor="secondary">
              {t("none")}
            </Text>
          )}
        </Flex>
      );
    }
    case RuleConditionType.HAS_TAG: {
      const tags = valuesAtExecution.conversation?.tags;
      if (!tags) {
        return null;
      }
      return (
        <Flex align="center" gap="xs" w="full" wrap="wrap">
          <Text fontSize="xs" textColor="secondary">
            {t("Current tags:")}
          </Text>
          {tags.length > 0 ? (
            tags.map((tag) => (
              <RedoBadge color="blue" key={tag} size="sm" text={tag} />
            ))
          ) : (
            <Text fontSize="xs" textColor="secondary">
              {t("none")}
            </Text>
          )}
        </Flex>
      );
    }
    case RuleConditionType.TRIGGER_TAG: {
      const tags = valuesAtExecution.conversation?.tags;
      if (!tags || tags.length === 0) {
        return null;
      }
      return (
        <Flex align="center" gap="xs" w="full" wrap="wrap">
          <Text fontSize="xs" textColor="secondary">
            {t("Trigger tag:")}
          </Text>
          {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
          <RedoBadge color="blue" size="sm" text={tags[0]!} />
        </Flex>
      );
    }
    case RuleConditionType.TOPIC: {
      const topics = valuesAtExecution.conversation?.topics;
      if (!topics) {
        return null;
      }
      return (
        <Flex align="center" gap="xs" w="full" wrap="wrap">
          <Text fontSize="xs" textColor="secondary">
            {t("Current topics:")}
          </Text>
          {topics.length > 0 ? (
            topics.map((topic) => (
              <RedoBadge color="blue" key={topic} size="sm" text={topic} />
            ))
          ) : (
            <Text fontSize="xs" textColor="secondary">
              {t("none")}
            </Text>
          )}
        </Flex>
      );
    }
    case RuleConditionType.MESSAGE_SENTIMENT: {
      const sentiment = valuesAtExecution.conversation?.sentiment;
      return (
        <Flex align="center" gap="xs" w="full">
          <Text fontSize="xs" textColor="secondary">
            {t("Current sentiment:")}
          </Text>
          <RedoBadge color="blue" size="sm" text={sentiment ?? t("unknown")} />
        </Flex>
      );
    }
    case RuleConditionType.MESSAGE_BODY_MATCHES: {
      const body = valuesAtExecution.message?.body;
      if (body === undefined) {
        return null;
      }
      return (
        <Flex align="flex-start" dir="column" gap="xs" w="full">
          <Text fontSize="xs" textColor="secondary">
            {t("Message body:")}
          </Text>
          {body ? (
            <Text fontSize="xs" textColor="primary">
              {body.length > 200 ? `${body.slice(0, 200)}…` : body}
            </Text>
          ) : (
            <Text fontSize="xs" textColor="secondary">
              {t("(empty)")}
            </Text>
          )}
        </Flex>
      );
    }
    case RuleConditionType.SENT_FROM: {
      const fromAddresses = valuesAtExecution.message?.fromAddresses;
      if (!fromAddresses) {
        return null;
      }
      return (
        <Flex align="center" gap="xs" w="full" wrap="wrap">
          <Text fontSize="xs" textColor="secondary">
            {t("Sent from:")}
          </Text>
          {fromAddresses.length > 0 ? (
            fromAddresses.map((addr) => (
              <RedoBadge color="blue" key={addr} size="sm" text={addr} />
            ))
          ) : (
            <Text fontSize="xs" textColor="secondary">
              {t("none")}
            </Text>
          )}
        </Flex>
      );
    }
    case RuleConditionType.RECEIVED_AT: {
      const toAddresses = valuesAtExecution.message?.toAddresses;
      if (!toAddresses) {
        return null;
      }
      return (
        <Flex align="center" gap="xs" w="full" wrap="wrap">
          <Text fontSize="xs" textColor="secondary">
            {t("Received at:")}
          </Text>
          {toAddresses.length > 0 ? (
            toAddresses.map((addr) => (
              <RedoBadge color="blue" key={addr} size="sm" text={addr} />
            ))
          ) : (
            <Text fontSize="xs" textColor="secondary">
              {t("none")}
            </Text>
          )}
        </Flex>
      );
    }
    case RuleConditionType.RESPONSE_TIME: {
      const lastCustomerAt =
        valuesAtExecution.conversation?.lastCustomerMessageAt;
      const lastMerchantAt =
        valuesAtExecution.conversation?.lastMerchantMessageAt;
      if (lastCustomerAt === undefined && lastMerchantAt === undefined) {
        return null;
      }
      return (
        <Flex align="flex-start" dir="column" gap="xs" w="full">
          {lastCustomerAt !== undefined && (
            <Text fontSize="xs" textColor="secondary">
              {lastCustomerAt
                ? t("Last customer message: {{date}}", {
                    date: new Date(lastCustomerAt).toLocaleString(),
                  })
                : t("Last customer message: never")}
            </Text>
          )}
          {lastMerchantAt !== undefined && (
            <Text fontSize="xs" textColor="secondary">
              {lastMerchantAt
                ? t("Last merchant message: {{date}}", {
                    date: new Date(lastMerchantAt).toLocaleString(),
                  })
                : t("Last merchant message: never")}
            </Text>
          )}
        </Flex>
      );
    }
    case RuleConditionType.LAST_ORDER: {
      const lastOrder = valuesAtExecution.lastOrder;
      if (!lastOrder) {
        return null;
      }
      return (
        <Flex align="flex-start" dir="column" gap="xs" w="full">
          {lastOrder.time !== undefined && (
            <Text fontSize="xs" textColor="secondary">
              {t("Order date: {{date}}", {
                date: new Date(lastOrder.time).toLocaleString(),
              })}
            </Text>
          )}
          {lastOrder.value !== undefined && (
            <Text fontSize="xs" textColor="secondary">
              {t("Order total: ${{amount}}", {
                amount: lastOrder.value.toFixed(2),
              })}
            </Text>
          )}
          {lastOrder.itemCount !== undefined && (
            <Text fontSize="xs" textColor="secondary">
              {t("Item count: {{count}}", { count: lastOrder.itemCount })}
            </Text>
          )}
        </Flex>
      );
    }
    case RuleConditionType.TIME_BASED: {
      const currentTime = valuesAtExecution.currentTime;
      if (!currentTime) {
        return null;
      }
      return (
        <Flex align="center" gap="xs" w="full">
          <Text fontSize="xs" textColor="secondary">
            {t("Evaluated at:")}
          </Text>
          <Text fontSize="xs" textColor="primary">
            {new Date(currentTime).toLocaleString()}
          </Text>
        </Flex>
      );
    }
    case RuleConditionType.MESSAGE_COUNT: {
      const messageCount = valuesAtExecution.conversation?.messageCount;
      if (messageCount === undefined) {
        return null;
      }
      return (
        <Flex align="center" gap="xs" w="full">
          <Text fontSize="xs" textColor="secondary">
            {t("Message count:")}
          </Text>
          <Text fontSize="xs" textColor="primary">
            {messageCount}
          </Text>
        </Flex>
      );
    }
    case RuleConditionType.ASSIGNMENT: {
      const assignee = valuesAtExecution.conversation?.assigneeType;
      if (assignee === undefined) {
        return null;
      }
      return (
        <Flex align="center" gap="xs" w="full">
          <Text fontSize="xs" textColor="secondary">
            {t("Current:")}
          </Text>
          <RedoBadge
            color="blue"
            size="sm"
            text={assignee ?? t("No assignee")}
          />
        </Flex>
      );
    }
    case RuleConditionType.MESSAGE_TYPE: {
      const channel = valuesAtExecution.message?.channel;
      if (channel === undefined) {
        return null;
      }
      return (
        <Flex align="center" gap="xs" w="full">
          <Text fontSize="xs" textColor="secondary">
            {t("Message type:")}
          </Text>
          <RedoBadge color="blue" size="sm" text={channel} />
        </Flex>
      );
    }
    case RuleConditionType.AI_STATUS: {
      const statuses = valuesAtExecution.threads?.aiStatuses;
      if (!statuses) {
        return null;
      }
      return (
        <Flex align="center" gap="xs" w="full" wrap="wrap">
          <Text fontSize="xs" textColor="secondary">
            {t("AI statuses:")}
          </Text>
          {statuses.length > 0 ? (
            statuses.map((s) => (
              <RedoBadge color="blue" key={s} size="sm" text={s} />
            ))
          ) : (
            <Text fontSize="xs" textColor="secondary">
              {t("none")}
            </Text>
          )}
        </Flex>
      );
    }
    case RuleConditionType.RANDOM_PERCENTAGE: {
      const rp = valuesAtExecution.randomPercentage;
      if (!rp || rp.rolled === undefined || rp.threshold === undefined) {
        return null;
      }
      return (
        <Flex align="center" gap="xs" w="full">
          <Text fontSize="xs" textColor="secondary">
            {t("Rolled {{rolled}} vs threshold {{threshold}}%", {
              rolled: rp.rolled.toFixed(1),
              threshold: rp.threshold,
            })}
          </Text>
        </Flex>
      );
    }
    case RuleConditionType.PREVIOUS_TICKET_STATUS: {
      const status = valuesAtExecution.conversation?.status;
      if (!status) {
        return null;
      }
      return (
        <Flex align="center" gap="xs" w="full">
          <Text fontSize="xs" textColor="secondary">
            {t("Previous:")}
          </Text>
          <RedoBadge color="blue" size="sm" text={status} />
        </Flex>
      );
    }
    // No value captured on the event at execution time.
    case RuleConditionType.AFTER_HOURS:
    case RuleConditionType.LAST_ORDER_TIME:
    case RuleConditionType.SENT_FROM_EMAIL:
    case RuleConditionType.RECEIVED_AT_EMAIL:
    case RuleConditionType.USER_IS_AVAILABLE:
    case RuleConditionType.MESSAGE_STORY_KIND:
      return null;
    case RuleConditionType.SHOPPER_AGENT_OUTREACH: {
      const outreachTypes = valuesAtExecution.shopperAgent?.outreachTypes;
      if (!outreachTypes) {
        return null;
      }
      return (
        <Flex align="center" gap="xs" w="full" wrap="wrap">
          <Text fontSize="xs" textColor="secondary">
            {t("Outreach:")}
          </Text>
          {outreachTypes.length > 0 ? (
            outreachTypes.map((outreachType) => (
              <RedoBadge
                color="blue"
                key={outreachType}
                size="sm"
                text={
                  shopperAgentOutreachConditionLabel(outreachType) ??
                  outreachType
                }
              />
            ))
          ) : (
            <Text fontSize="xs" textColor="secondary">
              {t("unrecognized")}
            </Text>
          )}
        </Flex>
      );
    }
    case RuleConditionType.SHOPPER_AGENT_CONVERSATION: {
      const linked = valuesAtExecution.shopperAgent?.conversationTopicLinked;
      if (linked === undefined) {
        return null;
      }
      return (
        <Flex align="center" gap="xs" w="full">
          <Text fontSize="xs" textColor="secondary">
            {t("{{productName}} conversation:", {
              productName: "Commerce Agent",
            })}
          </Text>
          <RedoBadge
            color={linked ? "blue" : "gray"}
            size="sm"
            text={linked ? t("yes") : t("no")}
          />
        </Flex>
      );
    }
    default:
      assertNever(conditionType);
  }
});
